/**
 * Cookie Consent Management
 * 
 * Manages user cookie preferences stored in localStorage.
 * Three categories: necessary (always on), analytics, marketing.
 * 
 * localStorage key: 9meals_cookie_consent
 * Format: { necessary: true, analytics: bool, marketing: bool, timestamp: ISO }
 */

const STORAGE_KEY = '9meals_cookie_consent';

const DEFAULT_CONSENT = {
  necessary: true,     // Always required
  analytics: false,    // GA, Amplitude, Contentsquare
  marketing: false,    // Future marketing cookies
};

/**
 * Get current consent state. Returns null if user hasn't made a choice yet.
 */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Check if user has made any consent choice
 */
export function hasConsentChoice() {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * Save consent preferences
 * @param {{ analytics: boolean, marketing: boolean }} prefs
 */
export function saveConsent(prefs) {
  const consent = {
    necessary: true,
    analytics: !!prefs.analytics,
    marketing: !!prefs.marketing,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  
  // Dispatch event so App.js can react
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));
  
  return consent;
}

/**
 * Accept all cookies
 */
export function acceptAll() {
  return saveConsent({ analytics: true, marketing: true });
}

/**
 * Reject all optional cookies (only necessary remains)
 */
export function rejectAll() {
  return saveConsent({ analytics: false, marketing: false });
}

/**
 * Check if analytics cookies are allowed
 */
export function isAnalyticsAllowed() {
  const consent = getConsent();
  return consent?.analytics === true;
}

/**
 * Check if marketing cookies are allowed
 */
export function isMarketingAllowed() {
  const consent = getConsent();
  return consent?.marketing === true;
}

/**
 * Load Google Analytics script dynamically
 */
export function loadGoogleAnalytics() {
  if (document.getElementById('gtag-script')) return;
  
  const script = document.createElement('script');
  script.id = 'gtag-script';
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-MRLCHB0FMK';
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', 'G-MRLCHB0FMK', { send_page_view: true });
}

/**
 * Load Contentsquare script dynamically
 */
export function loadContentsquare() {
  if (document.getElementById('cs-script')) return;
  
  const script = document.createElement('script');
  script.id = 'cs-script';
  script.src = 'https://t.contentsquare.net/uxa/a534c4c2de9a6.js';
  document.head.appendChild(script);
}

/**
 * Initialize all consented analytics. Called from App.js after consent.
 */
export function initConsentedAnalytics(initAmplitudeFn) {
  if (!isAnalyticsAllowed()) return;
  
  loadGoogleAnalytics();
  loadContentsquare();
  if (initAmplitudeFn) initAmplitudeFn();
}
